import { Button } from '@fluentui/react-components';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { useCallback, type FunctionComponent } from 'react';
/* eslint-disable-next-line owa-custom-rules/forbid-svg-import -- Direct SVG import required; component-based icon (@fluentui/react-icons) not available for this custom Rewards medallion illustration */
import REWARDS_MEDALLION_SVG from './RewardsMedallion.svg';
import {
    cookieFallbackDescription,
    cookieFallbackTitle,
    errorFallbackDescription,
    errorFallbackTitle,
    goToRewardsButton,
} from './RewardsFallback.locstring.json';
import {
    buttons,
    description,
    fallbackContainer,
    illustration,
    title,
} from './RewardsFallback.scss';

// Form/OCID tracking codes appended to the destination URLs so downstream analytics can attribute
// traffic that originates from this cookie-blocked fallback screen.
const GO_TO_REWARDS_URL = 'https://rewards.bing.com/?form=MY02S8&OCID=MY02S8';

// The fallback screen is shown for two distinct failure cases, differing only in the message text:
// 'cookiesBlocked' when third-party cookies prevent the flyout from loading, and 'error' when the
// flyout resolved a different account than the OWA user (identity mismatch) or otherwise failed.
export interface RewardsFallbackProps {
    variant?: 'cookiesBlocked' | 'error';
}

const defaultExport: FunctionComponent<RewardsFallbackProps> = observer(function RewardsFallback({
    variant = 'cookiesBlocked',
}: RewardsFallbackProps) {
    const onGoToRewards = useCallback(() => {
        window.open(GO_TO_REWARDS_URL, '_blank', 'noopener,noreferrer');
    }, []);

    const titleText = variant === 'error' ? errorFallbackTitle : cookieFallbackTitle;
    const descriptionText =
        variant === 'error' ? errorFallbackDescription : cookieFallbackDescription;

    return (
        <div className={fallbackContainer}>
            <img src={REWARDS_MEDALLION_SVG} alt="" aria-hidden="true" className={illustration} />
            <h2 className={title}>{loc(titleText)}</h2>
            <p className={description}>{loc(descriptionText)}</p>
            <div className={buttons}>
                <Button appearance="primary" onClick={onGoToRewards}>
                    {loc(goToRewardsButton)}
                </Button>
            </div>
        </div>
    );
},
'RewardsFallback');

export default defaultExport;
